import type {DimensionInventario, MedidaInventario} from '@cnc/contracts';
import {txt, type Texto} from '../i18n/idioma';

// Notas de ayuda (Daniel, 14/10/2026) de Inicio, Análisis, Cargar archivos,
// Campos y catálogos, Administración, Mi cuenta, Estado y Logros: qué es cada
// campo y cómo se llena. Cada texto se comprobó contra lo que hace la app.

// ── Inicio: tablero ─────────────────────────────────────────────────────────
export const AYUDA_INICIO = {
  bigBagsEnBodega: txt(
    'Big Bags que todavía no han salido (disponibles y asignados), de CNC y de Mineros. Los cuenta la app; al hacer clic se abren los disponibles en el inventario.',
    'Big Bags that have not left yet (available and assigned), from CNC and Mineros. The app counts them; clicking opens the available ones in the inventory.',
  ),
  tonSecasEnBodega: txt(
    'Suma de las toneladas secas de los Big Bags que siguen en bodega; debajo, las húmedas. Salen del peso de cada Big Bag.',
    'Sum of the dry tons of the Big Bags still in the warehouse; below, the wet tons. They come from the weight of each Big Bag.',
  ),
  oroEnBodega: txt(
    'Oro de los Big Bags que siguen en bodega: en onzas troy y, debajo, en gramos. Es la suma de lo que trae cada Big Bag.',
    'Gold in the Big Bags still in the warehouse: in troy ounces and, below, in grams. It is the sum of what each Big Bag carries.',
  ),
  plataEnBodega: txt(
    'Plata de los Big Bags que siguen en bodega: en onzas troy y, debajo, en gramos. Es la suma de lo que trae cada Big Bag.',
    'Silver in the Big Bags still in the warehouse: in troy ounces and, below, in grams. It is the sum of what each Big Bag carries.',
  ),
  exportador: txt(
    'Empresa exportadora de los Big Bags. "Sin exportador" reúne los que aún no tienen uno. Clic para verlos en el inventario.',
    'Exporting company of the Big Bags. "No exporter" groups those that have none yet. Click to see them in the inventory.',
  ),
  bigBagsTotal: txt(
    'Todos los Big Bags del exportador, en bodega y exportados.',
    'All Big Bags of the exporter, in the warehouse and exported.',
  ),
  enBodega: txt(
    'Big Bags del exportador que no han salido (disponibles y asignados).',
    'Big Bags of the exporter that have not left (available and assigned).',
  ),
  tonSecas: txt('Toneladas secas de todos sus Big Bags.', 'Dry tons of all its Big Bags.'),
  auOz: txt('Onzas troy de oro de todos sus Big Bags.', 'Troy ounces of gold of all its Big Bags.'),
  agOz: txt('Onzas troy de plata de todos sus Big Bags.', 'Troy ounces of silver of all its Big Bags.'),
  exportaciones: txt(
    'Todas las exportaciones de la app (propias, de terceros y de Mineros) y cuántas hay en cada estado. El estado se edita en el seguimiento o llega con las cargas de archivos.',
    'All exports in the app (own, third-party and Mineros) and how many are in each status. The status is edited in tracking or arrives with the file uploads.',
  ),
  historico: txt(
    'Todo lo que ha pasado por la app, en bodega y exportado.',
    'Everything that has gone through the app, in the warehouse and exported.',
  ),
  frescura: txt(
    'Fecha de la última carga guardada de cada archivo. Solo informa; al pasar el cursor por un nombre se ve el archivo que se cargó.',
    'Date of the latest saved upload of each file. It only informs; hovering over a name shows the file that was uploaded.',
  ),
} satisfies Record<string, Texto>;

// ── Inicio: pendientes de hoy ───────────────────────────────────────────────
export const AYUDA_PENDIENTES: Record<string, Texto> = {
  reservas: txt(
    'Reservas con tareas pendientes a 20, 16 o 3 días del zarpe. Las calcula la app con la fecha de salida de cada reserva; resaltadas, las que zarpan en 3 días o menos.',
    'Bookings with pending tasks 20, 16 or 3 days before departure. The app works them out from each booking’s departure date; highlighted, those leaving in 3 days or less.',
  ),
  propias: txt(
    'Alertas creadas a mano en el seguimiento que ya están en su ventana de aviso o vencieron.',
    'Alerts created by hand in tracking that are already in their notice window or overdue.',
  ),
  llegadas: txt(
    'Exportaciones que llegan hoy o cuya ETA ya pasó sin tener un estado final, y las que están en puerto con pocos días libres o ya con cobro. Lo calcula la app con la ETA, la llegada y los días libres.',
    'Exports arriving today or whose ETA has passed without a final status, and those in port with few free days left or already charged. The app works it out from the ETA, the arrival and the free days.',
  ),
  calidad: txt(
    'Alertas nuevas del agente de calidad: nadie las ha visto o aumentaron sus casos. Se quitan al marcarlas como vistas o al corregir los casos en Calidad de datos.',
    'New quality-agent alerts: nobody has seen them or their cases increased. They go away when marked as seen or when the cases are fixed in Data quality.',
  ),
  cumplimiento: txt(
    'Documentos de cumplimiento vencidos o por vencer. La app los calcula con la fecha de recepción, la vigencia del tipo de documento y sus días de aviso.',
    'Compliance documents that are expired or about to expire. The app works them out from the receipt date, the document type’s validity and its notice days.',
  ),
  navieras: txt(
    'Cambios de ETD, ETA o buque que trajo la revisión diaria de las navieras y que nadie ha revisado. Se revisan en Exportaciones → Navieras.',
    'ETD, ETA or vessel changes brought by the daily shipping-line check that nobody has reviewed. They are reviewed in Exports → Shipping lines.',
  ),
};

// ── Inicio: comparativo año contra año ──────────────────────────────────────
export const AYUDA_COMPARATIVO = {
  anio: txt(
    'Año que se mide. Si es el año en curso, cuenta del 1 de enero a hoy.',
    'Year being measured. If it is the current year, it counts from January 1 to today.',
  ),
  comparar: txt(
    'Año contra el que se compara. Sin elegir, el año anterior.',
    'Year it is compared with. If none is chosen, the previous year.',
  ),
  tipo: txt(
    'Qué exportaciones entran: propias, de terceros, de Mineros o todas. Las canceladas nunca cuentan. En Empacado las de terceros no tienen Big Bags.',
    'Which exports are included: own, third-party, Mineros or all. Cancelled ones never count. In Packed, third-party exports have no Big Bags.',
  ),
  exportador: txt(
    'Solo las exportaciones (o los Big Bags, en Empacado) de estos exportadores. Se pueden elegir varios.',
    'Only the exports (or the Big Bags, in Packed) of these exporters. Several can be chosen.',
  ),
  proveedor: txt(
    'Solo las exportaciones con al menos un Big Bag de estos proveedores; en Empacado, solo esos Big Bags.',
    'Only exports with at least one Big Bag from these suppliers; in Packed, only those Big Bags.',
  ),
  mina: txt(
    'Solo las exportaciones con al menos un Big Bag de estas minas; en Empacado, solo esos Big Bags.',
    'Only exports with at least one Big Bag from these mines; in Packed, only those Big Bags.',
  ),
  meses: txt(
    'Limita a estos meses del año: en Exportado, por mes de zarpe (ETD); en Empacado, por mes de empaque.',
    'Limits to these months of the year: in Exported, by departure month (ETD); in Packed, by packing month.',
  ),
  mirada: txt(
    'Exportado: exportaciones por su fecha de zarpe (ETD), sin canceladas. Empacado: Big Bags por su fecha de empaque.',
    'Exported: exports by their departure date (ETD), excluding cancelled ones. Packed: Big Bags by their packing date.',
  ),
  proyeccion: txt(
    'Estimación de los próximos meses: la mitad el ritmo mensual del año y la mitad lo que pasó ese mismo mes un año antes. No es un dato real.',
    'Estimate for the coming months: half the monthly pace of the year and half what happened in that same month a year earlier. It is not actual data.',
  ),
  tarjetas: txt(
    'Cada tarjeta muestra la cifra del año elegido, el mismo periodo del año comparado, la variación en porcentaje, el año comparado completo y el avance (qué parte de ese año ya se alcanzó). Clic en una tarjeta para verla en la gráfica.',
    'Each card shows the figure of the chosen year, the same period of the compared year, the change in percent, the full compared year and the progress (how much of that year has been reached). Click a card to see it in the chart.',
  ),
  vista: txt(
    'Mensual: lo de cada mes. Acumulado: la suma desde enero hasta cada mes.',
    'Monthly: each month on its own. Cumulative: the sum from January up to each month.',
  ),
  numero: txt('Número de la fila en la lista.', 'Row number in the list.'),
  codigo: txt(
    'Código de la exportación; abre su ficha en el seguimiento.',
    'Export code; opens its record in tracking.',
  ),
  estado: txt('Estado de la exportación en el seguimiento.', 'Status of the export in tracking.'),
  etd: txt(
    'Fecha de zarpe (ETD) de la exportación, del seguimiento.',
    'Departure date (ETD) of the export, from tracking.',
  ),
  listaBigBags: txt('Big Bags de la exportación.', 'Big Bags of the export.'),
  listaTonSecas: txt('Toneladas secas de la exportación.', 'Dry tons of the export.'),
  listaAuOz: txt('Onzas troy de oro de la exportación.', 'Troy ounces of gold of the export.'),
  listaAgOz: txt('Onzas troy de plata de la exportación.', 'Troy ounces of silver of the export.'),
  mes: txt('Mes proyectado.', 'Projected month.'),
  actual: txt(
    'Lo del año elegido; si está en curso, hasta hoy.',
    'The chosen year; if it is the current one, up to today.',
  ),
  mismoPeriodo: txt(
    'Lo del año comparado entre el 1 de enero y la misma fecha de hoy.',
    'The compared year between January 1 and today’s same date.',
  ),
  variacion: txt(
    'Cuánto sube o baja el año elegido frente al comparado, en porcentaje.',
    'How much the chosen year goes up or down against the compared one, in percent.',
  ),
  completo: txt('Todo el año comparado, de enero a diciembre.', 'The whole compared year, January to December.'),
  avance: txt(
    'Qué parte del año comparado completo ya se alcanzó este año.',
    'What part of the full compared year has already been reached this year.',
  ),
} satisfies Record<string, Texto>;

// Medidas del comparativo (tarjetas, columnas de la proyeccion).
export const AYUDA_MEDIDA_COMPARATIVO: Record<string, Texto> = {
  exportaciones: txt(
    'Cantidad de exportaciones con zarpe (ETD) en el periodo, sin las canceladas.',
    'Number of exports with departure (ETD) in the period, excluding cancelled ones.',
  ),
  contenedores: txt(
    'Contenedores de esas exportaciones que tienen Big Bags (uno vacío no cuenta); si una exportación no tiene Big Bags, cuentan los de su logística.',
    'Containers of those exports that have Big Bags (an empty one does not count); if an export has no Big Bags, those of its logistics count.',
  ),
  bigBags: txt(
    'Exportado: Big Bags de esas exportaciones. Empacado: Big Bags empacados en el periodo.',
    'Exported: Big Bags of those exports. Packed: Big Bags packed in the period.',
  ),
  tonSecas: txt(
    'Toneladas secas de esas exportaciones o de los Big Bags empacados.',
    'Dry tons of those exports or of the packed Big Bags.',
  ),
  tonHumedas: txt(
    'Toneladas húmedas de esas exportaciones o de los Big Bags empacados.',
    'Wet tons of those exports or of the packed Big Bags.',
  ),
  auOz: txt(
    'Onzas troy de oro de esas exportaciones o de los Big Bags empacados.',
    'Troy ounces of gold of those exports or of the packed Big Bags.',
  ),
  agOz: txt(
    'Onzas troy de plata de esas exportaciones o de los Big Bags empacados.',
    'Troy ounces of silver of those exports or of the packed Big Bags.',
  ),
};

// ── Análisis: reportes (tabla dinámica) ─────────────────────────────────────
export const AYUDA_REPORTES = {
  filas: txt(
    'Cómo se agrupan los Big Bags en las filas de la tabla (por exportador, mina, mes de molienda…).',
    'How the Big Bags are grouped in the table rows (by exporter, mine, grinding month…).',
  ),
  columnas: txt(
    'Un segundo grupo, opcional, que reparte cada fila en columnas. Con "Ninguna" cada medida va en su propia columna.',
    'An optional second group that splits each row into columns. With "None" each measure has its own column.',
  ),
  medidas: txt(
    'Qué se suma o promedia de los Big Bags de cada grupo. Se pueden elegir varias; con columnas, la tabla muestra la elegida en Medida.',
    'What is summed or averaged from the Big Bags of each group. Several can be chosen; with columns, the table shows the one chosen in Measure.',
  ),
  grafica: txt(
    'Barras o líneas. Con columnas, las barras se apilan.',
    'Bars or lines. With columns, the bars are stacked.',
  ),
  medida: txt(
    'La medida que se dibuja en la gráfica (y en la tabla cuando hay columnas).',
    'The measure drawn in the chart (and in the table when there are columns).',
  ),
} satisfies Record<string, Texto>;

export const AYUDA_DIMENSION: Record<DimensionInventario, Texto> = {
  linea: txt(
    'Inventario al que pertenece el Big Bag: CNC o Mineros.',
    'Inventory the Big Bag belongs to: CNC or Mineros.',
  ),
  estado: txt(
    'Estado del Big Bag: disponible (en bodega, sin exportación), asignado (con exportación, sin salir) o exportado.',
    'Status of the Big Bag: available (in the warehouse, no export), assigned (with an export, not shipped) or exported.',
  ),
  exportador: txt(
    'Empresa que exporta el Big Bag. Sale del archivo o de la regla del prefijo del BB #.',
    'Company that exports the Big Bag. It comes from the file or from the BB # prefix rule.',
  ),
  proveedor: txt(
    'Proveedor del Big Bag. Sale del archivo o de la regla del prefijo del BB #.',
    'Supplier of the Big Bag. It comes from the file or from the BB # prefix rule.',
  ),
  mina: txt(
    'Mina de origen del Big Bag. Sale del archivo o de la regla del prefijo del BB #.',
    'Mine of origin of the Big Bag. It comes from the file or from the BB # prefix rule.',
  ),
  planta: txt('Planta donde se procesó el material.', 'Plant where the material was processed.'),
  ubicacion: txt(
    'Bodega donde está o estuvo el Big Bag, según los archivos cargados.',
    'Warehouse where the Big Bag is or was, according to the uploaded files.',
  ),
  municipio: txt(
    'Municipio de origen del material, cuando el archivo lo trae.',
    'Municipality of origin of the material, when the file has it.',
  ),
  tipoMaterial: txt(
    'Tipo de material (FLO, JIG, MIX…). Viene del archivo; en Colombian Mint la app cambia MIX por FLO o JIG.',
    'Material type (FLO, JIG, MIX…). It comes from the file; for Colombian Mint the app changes MIX to FLO or JIG.',
  ),
  exportacion: txt(
    'Código de la exportación a la que está asignado el Big Bag.',
    'Code of the export the Big Bag is assigned to.',
  ),
  recoleccion: txt(
    'Recolección (Shipment #) con la que llegó el Big Bag.',
    'Pickup (Shipment #) the Big Bag came with.',
  ),
  mesMolienda: txt(
    'Mes de molienda oficial (columna MES MOLIENDA del Consolidated).',
    'Official grinding month (MES MOLIENDA column of the Consolidated).',
  ),
  mesEmpaque: txt('Mes de la fecha de empaque del Big Bag.', 'Month of the Big Bag’s packing date.'),
  mesEtd: txt(
    'Mes de zarpe (ETD) de la exportación del Big Bag; sin exportación queda sin dato.',
    'Departure month (ETD) of the Big Bag’s export; without an export it has no data.',
  ),
  anioEtd: txt(
    'Año de zarpe (ETD) de la exportación del Big Bag; sin exportación queda sin dato.',
    'Departure year (ETD) of the Big Bag’s export; without an export it has no data.',
  ),
  mesEta: txt(
    'Mes de llegada (ETA) de la exportación del Big Bag; sin exportación queda sin dato.',
    'Arrival month (ETA) of the Big Bag’s export; without an export it has no data.',
  ),
  anioEta: txt(
    'Año de llegada (ETA) de la exportación del Big Bag; sin exportación queda sin dato.',
    'Arrival year (ETA) of the Big Bag’s export; without an export it has no data.',
  ),
};

export const AYUDA_MEDIDA: Record<MedidaInventario, Texto> = {
  bigBags: txt('Cantidad de Big Bags del grupo.', 'Number of Big Bags in the group.'),
  tonHumedas: txt('Suma de las toneladas húmedas de cada Big Bag.', 'Sum of the wet tons of each Big Bag.'),
  tonSecas: txt('Suma de las toneladas secas de cada Big Bag.', 'Sum of the dry tons of each Big Bag.'),
  humedadTon: txt(
    'Agua del material: toneladas húmedas menos toneladas secas.',
    'Water in the material: wet tons minus dry tons.',
  ),
  humedadPorcentaje: txt(
    'Humedad en toneladas ÷ toneladas húmedas × 100. Es un porcentaje: no se suma entre columnas.',
    'Moisture in tons ÷ wet tons × 100. It is a percentage: it is not added across columns.',
  ),
  auG: txt('Suma de los gramos de oro de cada Big Bag.', 'Sum of the grams of gold of each Big Bag.'),
  agG: txt('Suma de los gramos de plata de cada Big Bag.', 'Sum of the grams of silver of each Big Bag.'),
  auOz: txt('Suma de las onzas troy de oro de cada Big Bag.', 'Sum of the troy ounces of gold of each Big Bag.'),
  agOz: txt('Suma de las onzas troy de plata de cada Big Bag.', 'Sum of the troy ounces of silver of each Big Bag.'),
  auGtPromedio: txt(
    'Tenor de oro del grupo: gramos de oro ÷ toneladas secas. Es un promedio: no se suma entre columnas.',
    'Gold grade of the group: grams of gold ÷ dry tons. It is an average: it is not added across columns.',
  ),
  agGtPromedio: txt(
    'Tenor de plata del grupo: gramos de plata ÷ toneladas secas. Es un promedio: no se suma entre columnas.',
    'Silver grade of the group: grams of silver ÷ dry tons. It is an average: it is not added across columns.',
  ),
};

// ── Análisis: informes ──────────────────────────────────────────────────────
export const AYUDA_INFORMES = {
  conHistorial: txt(
    'Suma los Big Bags cargados de los consolidados viejos (2020-2023). Sin marcar, el informe sale como los archivos vigentes.',
    'Adds the Big Bags loaded from the old consolidated files (2020-2023). Unticked, the report comes out like the current files.',
  ),
  informe: txt('Nombre del informe que se generó.', 'Name of the report that was generated.'),
  generado: txt('Fecha y hora en que se generó.', 'Date and time it was generated.'),
  por: txt('Persona que lo generó.', 'Person who generated it.'),
  filas: txt('Filas de datos que trae el libro de Excel.', 'Data rows in the Excel workbook.'),
  tardo: txt('Tiempo que tardó el servidor en armarlo.', 'Time the server took to build it.'),
  archivo: txt(
    'Libro de Excel generado; queda guardado en el historial y se descarga con un clic.',
    'Generated Excel workbook; it is kept in the history and downloads with one click.',
  ),
} satisfies Record<string, Texto>;

// ── Análisis: calidad de datos ──────────────────────────────────────────────
export const AYUDA_CALIDAD = {
  errores: txt(
    'Suma de los casos de todas las revisiones de tipo error. Los casos aceptados no cuentan.',
    'Sum of the cases of all error checks. Accepted cases do not count.',
  ),
  advertencias: txt(
    'Suma de los casos de todas las revisiones de tipo advertencia. Los casos aceptados no cuentan.',
    'Sum of the cases of all warning checks. Accepted cases do not count.',
  ),
  informativas: txt(
    'Suma de los casos de las revisiones informativas: datos para saber, no errores.',
    'Sum of the cases of the informational checks: things to know, not errors.',
  ),
  bigBags: txt('Todos los Big Bags de la base, de CNC y de Mineros.', 'All Big Bags in the database, CNC and Mineros.'),
  exportaciones: txt('Todas las exportaciones de la base.', 'All exports in the database.'),
  agente: txt(
    'Revisa los datos solo cada cierto tiempo y deja una alerta por cada revisión de error o advertencia que tenga casos. "Nueva" = nadie la ha visto o aumentaron sus casos.',
    'Checks the data on its own every so often and leaves an alert for each error or warning check that has cases. "New" = nobody has seen it or its cases increased.',
  ),
  nota: txt(
    'Por qué los casos marcados están bien. Queda guardada junto con la aceptación.',
    'Why the ticked cases are fine. It is saved along with the acceptance.',
  ),
  severidad: txt(
    'Qué tan grave es: error, advertencia o información.',
    'How serious it is: error, warning or information.',
  ),
  categoria: txt('Tipo de incidencia que dejó la carga.', 'Kind of issue the upload left.'),
  registro: txt(
    'Big Bag o exportación a la que se refiere; clic para abrir su ficha.',
    'Big Bag or export it refers to; click to open its record.',
  ),
  campo: txt('Dato del archivo que tuvo la incidencia.', 'Data field in the file that had the issue.'),
  archivoDecia: txt('Valor tal como venía en el archivo.', 'Value exactly as it came in the file.'),
  quedo: txt('Valor que guardó la app después de la carga.', 'Value the app saved after the upload.'),
  mensaje: txt('Explicación de la incidencia, escrita por la app.', 'Explanation of the issue, written by the app.'),
  decision: txt(
    'Lo que se decidió sobre la incidencia; vacío si nadie la ha decidido.',
    'What was decided about the issue; empty if nobody has decided it.',
  ),
  prefijoBb: txt(
    'Letras con que empieza el BB #. Con ellas la app asigna mina, proveedor y exportador.',
    'Letters the BB # starts with. With them the app assigns mine, supplier and exporter.',
  ),
  minaRegla: txt('Mina que la regla asigna a ese prefijo.', 'Mine the rule assigns to that prefix.'),
  proveedorRegla: txt('Proveedor que la regla asigna a ese prefijo.', 'Supplier the rule assigns to that prefix.'),
  exportadorRegla: txt('Exportador que la regla asigna.', 'Exporter the rule assigns.'),
  bbPrefijo: txt('Big Bags de la base con ese prefijo.', 'Big Bags in the database with that prefix.'),
  proveedor: txt('Proveedor de los Big Bags.', 'Supplier of the Big Bags.'),
  bbProveedor: txt('Big Bags de la base de ese proveedor.', 'Big Bags in the database from that supplier.'),
  prefijoExportacion: txt('Letras con que empieza el código de exportación.', 'Letters the export code starts with.'),
  activo: txt(
    'Si está apagado, la regla no se usa. Se cambia en Exportadores por prefijo.',
    'If off, the rule is not used. It is changed in Exporters by prefix.',
  ),
} satisfies Record<string, Texto>;

// ── Historial de cargas (Cargar archivos y Calidad de datos) ────────────────
export const AYUDA_CARGAS = {
  fecha: txt('Cuándo se subió el archivo.', 'When the file was uploaded.'),
  tipo: txt('Qué archivo o carga fue.', 'Which file or upload it was.'),
  archivo: txt('Nombre del archivo subido.', 'Name of the uploaded file.'),
  estado: txt(
    'Recibida, Revisada sin aplicar, Aplicada o Fallida (con el motivo). Una carga solo cambia datos cuando queda Aplicada.',
    'Received, Reviewed not applied, Applied or Failed (with the reason). An upload only changes data once it is Applied.',
  ),
  filas: txt('Filas leídas del archivo.', 'Rows read from the file.'),
  nuevasActualizadas: txt(
    'Registros que la carga creó / registros que cambió. Guion si la carga no lo informa.',
    'Records the upload created / records it changed. A dash if the upload does not report it.',
  ),
  incidencias: txt('Avisos que dejó la carga.', 'Issues the upload left.'),
  sinDecision: txt(
    'Incidencias de error o advertencia que nadie ha decidido.',
    'Error or warning issues nobody has decided.',
  ),
} satisfies Record<string, Texto>;

// ── Campos y catálogos ──────────────────────────────────────────────────────
export const AYUDA_PREFIJOS = {
  prefijo: txt(
    'Letras con que empiezan los códigos de exportación (GLC para GLC-01). Se escribe al agregar la fila y después no cambia. Si un código encaja con varios, manda el más largo.',
    'Letters the export codes start with (GLC for GLC-01). It is typed when adding the row and does not change afterwards. If a code matches several, the longest wins.',
  ),
  exportador: txt(
    'Empresa que exporta los códigos con ese prefijo. Se elige de la lista o se escribe; si no existe, se crea como exportador.',
    'Company that exports the codes with that prefix. Pick it from the list or type it; if it does not exist, it is created as an exporter.',
  ),
  exportaciones: txt(
    'Exportaciones de la app que toman este prefijo. Las cuenta la app.',
    'Exports in the app that use this prefix. The app counts them.',
  ),
  activo: txt(
    'Apagado, la regla deja de usarse y esas exportaciones no toman este exportador.',
    'Off, the rule stops being used and those exports do not take this exporter.',
  ),
  notas: txt('Comentario libre sobre el prefijo.', 'Free comment about the prefix.'),
  sinAsignar: txt(
    'Inicios de código que la app encontró sin exportador; el número es cuántas exportaciones lo usan. Clic para agregarlo a la tabla.',
    'Code starts the app found without an exporter; the number is how many exports use it. Click to add it to the table.',
  ),
} satisfies Record<string, Texto>;

export const AYUDA_EMPRESAS = {
  buscar: txt('Busca por el nombre o por los otros nombres.', 'Searches by the name or by the other names.'),
  empresa: txt(
    'Nombre principal de la empresa. Se crea con las cargas de archivos o a mano.',
    'Main name of the company. It is created by file uploads or by hand.',
  ),
  roles: txt(
    'Tipos de la empresa: puede ser varios a la vez (proveedor, exportador, comprador, cliente, trader, importador, naviera…). Se marcan en su ficha; los que salen de los datos los pone la app sola.',
    'Types of the company: it can be several at once (supplier, exporter, buyer, client, trader, importer, shipping line…). They are ticked in its record; the ones that come from the data are set by the app.',
  ),
  filtroTipo: txt('Muestra solo las empresas de ese tipo.', 'Shows only the companies of that type.'),
  nombre: txt(
    'Nombre completo de la empresa, como debe salir en los documentos. Si cambias el nombre, el anterior queda como otro nombre para que las cargas la sigan reconociendo.',
    'Full name of the company, as it must appear in documents. If you change the name, the old one stays as another name so uploads still recognise it.',
  ),
  abreviacion: txt(
    'Nombre corto o código de la empresa (por ejemplo NIM). No se puede repetir: ninguna otra empresa puede tenerlo como nombre, abreviación u otro nombre. Las cargas también la reconocen por aquí.',
    'Short name or code of the company (for example NIM). It cannot repeat: no other company can have it as name, abbreviation or other name. Uploads also recognise it by this.',
  ),
  identificacion: txt(
    'NIT, Tax ID u otra identificación tributaria, si la tiene. Se escribe tal como sale en sus documentos.',
    'NIT, Tax ID or other tax identification, if it has one. Type it as it appears in its documents.',
  ),
  tipos: txt(
    'Marca todos los que aplican. Los que tienen ✓ gris salen de los datos de la app (por ejemplo, es el exportador de una exportación) y no se pueden quitar aquí.',
    'Tick all that apply. The ones with a grey ✓ come from the app data (for example, it is the exporter of an export) and cannot be removed here.',
  ),
  direccion: txt(
    'Dirección principal de la empresa (calle, ciudad y país).',
    'Main address of the company (street, city and country).',
  ),
  entrega: txt(
    'Dirección a donde se le entrega la carga o los documentos. Hay espacio para tres; deja vacías las que no use.',
    'Address where the cargo or documents are delivered to it. There is room for three; leave empty the ones it does not use.',
  ),
  idioma: txt('Idioma en que se le escribe a la empresa.', 'Language in which the company is written to.'),
  contactoNombre: txt('Persona con quien se habla en la empresa.', 'Person who is the contact at the company.'),
  contactoTelefono: txt(
    'Teléfono del contacto, con el indicativo del país (por ejemplo +57).',
    'Phone of the contact, with the country code (for example +57).',
  ),
  contactoCorreo: txt('Correo del contacto.', 'Email of the contact.'),
  notas: txt('Lo que el equipo deba saber de esta empresa.', 'Anything the team should know about this company.'),
  activa: txt(
    'Si la desmarcas, la empresa se conserva con todo su historial pero no se ofrece en las listas.',
    'If you untick it, the company is kept with all its history but it is not offered in the lists.',
  ),
  abreviacionTabla: txt('Abreviación única de la empresa.', 'Unique abbreviation of the company.'),
  identificacionTabla: txt('NIT o Tax ID de la empresa.', 'NIT or Tax ID of the company.'),
  ficha: txt(
    'Abre la ficha de la empresa para ver o cambiar sus datos: tipos, identificación, direcciones, idioma y contacto.',
    'Opens the record of the company to see or change its data: types, identification, addresses, language and contact.',
  ),
  otrosNombres: txt(
    'Nombres o códigos con que también aparece en los archivos. Quedan al unir empresas o al guardarlos en Otros nombres.',
    'Names or codes it also appears with in the files. They are kept when merging companies or saving them in Other names.',
  ),
  exportaciones: txt('Exportaciones en las que es el exportador.', 'Exports in which it is the exporter.'),
  bigBags: txt(
    'Big Bags en los que es el exportador o el proveedor.',
    'Big Bags in which it is the exporter or the supplier.',
  ),
  unirCon: txt(
    'Empresa correcta con la que se une esta: todo pasa a ella y este nombre queda como otro nombre suyo.',
    'Right company this one is merged into: everything moves there and this name becomes another of its names.',
  ),
  unirMarcadas: txt('Empresa con la que se unen todas las marcadas.', 'Company all the ticked ones are merged into.'),
  empresaAlias: txt(
    'Nombre principal de la empresa. Si no existe, se crea.',
    'Main name of the company. If it does not exist, it is created.',
  ),
  alias: txt(
    'Códigos o nombres con que la empresa sale en los archivos, separados por coma. Desde entonces las cargas la reconocen.',
    'Codes or names the company appears with in the files, separated by commas. From then on the uploads recognise it.',
  ),
} satisfies Record<string, Texto>;

// ── Administración ──────────────────────────────────────────────────────────
export const AYUDA_USUARIOS = {
  accesos: txt(
    'Qué secciones y pestañas ve o edita esta persona. Por sección se elige No ve, Ve o Ve y edita; cada pestaña sigue a su sección o tiene su propio nivel (por ejemplo, Inventario: No ve y Mineros: Ve, para que solo vea Mineros). Consulta nunca edita. Los administradores tienen todo. La app lo comprueba en cada consulta, no solo en el menú.',
    'Which sections and tabs this person sees or edits. For each section choose No access, View or View and edit; each tab follows its section or has its own level (for example, Inventory: No access and Mineros: View, so they only see Mineros). Viewers never edit. Administrators have everything. The app checks it on every request, not only in the menu.',
  ),
  nombre: txt(
    'Nombre completo de la persona, como se verá en la bitácora.',
    'Full name of the person, as it will show in the audit log.',
  ),
  correo: txt('Correo con el que la persona entra a la app.', 'Email the person signs in with.'),
  rol: txt(
    'Administrador: todo. Edición: consulta, edita y carga archivos. Consulta: solo ve y genera reportes.',
    'Administrator: everything. Editing: views, edits and uploads files. Read-only: only views and generates reports.',
  ),
  rolTabla: txt(
    'Se cambia aquí y cambiarlo cierra sus sesiones. Nadie puede cambiar su propio rol.',
    'It is changed here and changing it closes their sessions. Nobody can change their own role.',
  ),
  estado: txt(
    'Activo o desactivado. También avisa si está bloqueado por intentos fallidos o si debe cambiar la contraseña.',
    'Active or deactivated. It also says if it is locked after failed attempts or must change the password.',
  ),
  dosPasos: txt(
    'Si la persona activó el código del teléfono para entrar. Cada uno la activa en Mi cuenta.',
    'Whether the person turned on the phone code to sign in. Each person turns it on in My account.',
  ),
  ultimoIngreso: txt('Última vez que la persona entró.', 'Last time the person signed in.'),
  sesiones: txt(
    'Sesiones abiertas ahora: computadores o navegadores donde sigue dentro.',
    'Sessions open now: computers or browsers where they are still signed in.',
  ),
} satisfies Record<string, Texto>;

export const AYUDA_BITACORA = {
  usuario: txt('Solo los cambios de esta persona.', 'Only the changes made by this person.'),
  accion: txt(
    'Tipo de cambio; entre paréntesis, cuántas veces aparece.',
    'Kind of change; in brackets, how many times it appears.',
  ),
  desde: txt('Solo los cambios desde este día.', 'Only the changes from this day on.'),
  hasta: txt('Solo los cambios hasta este día.', 'Only the changes up to this day.'),
  buscar: txt(
    'Busca en el registro afectado y en los detalles del cambio (código, correo, archivo…).',
    'Searches the affected record and the change details (code, email, file…).',
  ),
  fecha: txt('Cuándo se hizo el cambio.', 'When the change was made.'),
  quien: txt(
    'Persona que hizo el cambio. "Sistema" si lo hizo la app sola.',
    'Person who made the change. "System" if the app did it on its own.',
  ),
  que: txt(
    'Acción y, si la tiene, de dónde vino (pantalla, Excel, carga…).',
    'Action and, if it has one, where it came from (screen, Excel, upload…).',
  ),
  sobre: txt(
    'Registro afectado: código de exportación, BB #, correo… Una exportación se abre con un clic.',
    'Affected record: export code, BB #, email… An export opens with one click.',
  ),
  cambios: txt('Valor anterior, tachado → valor nuevo.', 'Previous value, struck through → new value.'),
  deshacer: txt(
    'Cualquier cambio en los datos se puede deshacer: ediciones, eliminaciones, cargas de archivos, uniones... (no lo de las cuentas de usuario ni los documentos generados). Primero muestra qué vuelve; nunca pisa un cambio posterior y queda como un cambio nuevo, que también se puede deshacer.',
    'Any data change can be undone: edits, deletions, file uploads, merges... (not user accounts or generated documents). It first shows what comes back; it never overwrites a later change and is recorded as a new change, which can also be undone.',
  ),
} satisfies Record<string, Texto>;

export const AYUDA_CONFIGURACION = {
  dosPasos: txt(
    'Marcado, ningún administrador entra sin el código de su teléfono. Solo lo cambia un administrador.',
    'Ticked, no administrator signs in without their phone code. Only an administrator can change it.',
  ),
  horas: txt(
    'Horas sin usar la app tras las cuales la sesión se cierra sola. De 1 a 168.',
    'Hours without using the app after which the session closes on its own. From 1 to 168.',
  ),
} satisfies Record<string, Texto>;

export const AYUDA_DOCUMENTOS = {
  deLaEmpresa: txt(
    'Logo, firma y certificado de ensayo propios de esta empresa. Los documentos Bluequest toman el logo y la firma del vendedor (la empresa de la factura, por ejemplo NIM en NIMBLQ-58) y el certificado de ensayo del exportador de la exportación. Si la empresa no tiene el suyo, se usa el general de Administración → Documentos.',
    "Logo, signature and assay certificate of this company. The Bluequest documents take the logo and signature of the seller (the invoice company, for example NIM in NIMBLQ-58) and the assay certificate of the export's exporter. If the company has none of its own, the general one from Administration → Documents is used.",
  ),
  enUso: txt(
    'Versión con la que se generan hoy los documentos: tamaño, fecha de subida y quién la subió. Clic en el nombre para descargarla.',
    'Version used today to generate the documents: size, upload date and who uploaded it. Click the name to download it.',
  ),
  subir: txt(
    'Archivo de la plantilla en el formato indicado. Se revisa al subirlo y queda como versión nueva en uso; las anteriores se guardan.',
    'Template file in the stated format. It is checked on upload and becomes the new version in use; previous ones are kept.',
  ),
  nota: txt(
    'Qué cambió en esta versión; queda en la lista de versiones.',
    'What changed in this version; it stays in the list of versions.',
  ),
  firmanteNombre: txt(
    'Nombre completo de quien firma la carta DIAN y la lista de empaque.',
    'Full name of the person who signs the DIAN letter and the packing list.',
  ),
  firmanteCargo: txt(
    'Cargo de quien firma la carta DIAN y la lista de empaque.',
    'Position of the person who signs the DIAN letter and the packing list.',
  ),
  firmanteNombreCorto: txt(
    'Nombre del firmante al pie de la carta aclaratoria.',
    'Signer name at the foot of the clarification letter.',
  ),
  firmanteCargoCorto: txt(
    'Cargo del firmante al pie de la carta aclaratoria.',
    'Signer position at the foot of the clarification letter.',
  ),
  vendedorNombre: txt(
    'Nombre del vendedor en el encabezado de los documentos de aduana.',
    'Seller name in the header of the customs documents.',
  ),
  vendedorRazonSocial: txt(
    'Razón social que nombra el texto de la carta aclaratoria.',
    'Legal name used in the text of the clarification letter.',
  ),
  vendedorDireccion: txt(
    'Dirección del vendedor en el encabezado de los documentos de aduana.',
    'Seller address in the header of the customs documents.',
  ),
  vendedorCiudad: txt(
    'Ciudad del vendedor en el encabezado de los documentos de aduana.',
    'Seller city in the header of the customs documents.',
  ),
  vendedorNit: txt(
    'NIT del vendedor en el encabezado de los documentos de aduana.',
    'Seller NIT in the header of the customs documents.',
  ),
  consignatarioLineas: txt(
    'Datos de la empresa compradora, renglón por renglón, como salen en la lista de empaque.',
    'Details of the buying company, line by line, as they appear on the packing list.',
  ),
  consignatarioCarta: txt(
    'Cómo nombra la carta aclaratoria a la empresa compradora.',
    'How the clarification letter names the buying company.',
  ),
  textoFirmante: txt(
    'Texto exacto de la plantilla de la carta aclaratoria que la app cambia por el nombre del firmante.',
    'Exact text in the clarification letter template that the app replaces with the signer name.',
  ),
  textoCargo: txt(
    'Texto exacto de la plantilla de la carta aclaratoria que la app cambia por el cargo del firmante.',
    'Exact text in the clarification letter template that the app replaces with the signer position.',
  ),
} satisfies Record<string, Texto>;

// ── Mi cuenta ───────────────────────────────────────────────────────────────
export const AYUDA_CUENTA = {
  quienSoy: txt(
    'Tu nombre, correo y rol. Los registra un administrador en Usuarios.',
    'Your name, email and role. An administrator records them in Users.',
  ),
  actual: txt(
    'La contraseña con la que entras hoy (o la temporal que te entregaron).',
    'The password you sign in with today (or the temporary one you were given).',
  ),
  nueva: txt(
    'La que usarás desde ahora: con letras y números y del largo mínimo indicado arriba. Solo tú la conoces.',
    'The one you will use from now on: with letters and numbers and the minimum length stated above. Only you know it.',
  ),
  repetida: txt('Escríbela otra vez para evitar errores de digitación.', 'Type it again to avoid typing mistakes.'),
  ultimoIngreso: txt(
    'Fecha y hora del último ingreso registrado con tu cuenta.',
    'Date and time of the last sign-in recorded with your account.',
  ),
  codigo: txt(
    'Código de 6 dígitos que muestra ahora la app de autenticación de tu teléfono; cambia cada 30 segundos.',
    'Six-digit code shown right now by the authenticator app on your phone; it changes every 30 seconds.',
  ),
  dosPasos: txt(
    'Si tu cuenta pide el código del teléfono al entrar. La activas o desactivas aquí.',
    'Whether your account asks for the phone code when signing in. You turn it on or off here.',
  ),
  idioma: txt('Idioma de las pantallas. Queda en tu perfil.', 'Language of the screens. It is saved in your profile.'),
  tema: txt(
    'Claro, oscuro o como el sistema. Queda en tu perfil.',
    'Light, dark or same as system. It is saved in your profile.',
  ),
  avisosDeLogros: txt(
    'Encendido si no lo cambias. Queda en tu perfil.',
    'On unless you change it. It is saved in your profile.',
  ),
  descansaPantalla: txt(
    'Encendido si no lo cambias. Queda en tu perfil.',
    'On unless you change it. It is saved in your profile.',
  ),
} satisfies Record<string, Texto>;

// ── Estado del sistema ──────────────────────────────────────────────────────
export const AYUDA_ESTADO = {
  estado: txt(
    'Resumen de la revisión del servidor: "Todo en orden" o "Con problemas".',
    'Summary of the server check: "All good" or "Has problems".',
  ),
  version: txt('Versión de la app instalada en el servidor.', 'Version of the app installed on the server.'),
  entorno: txt(
    'Producción es la app que usa el equipo; Desarrollo, una copia de trabajo.',
    'Production is the app the team uses; Development, a working copy.',
  ),
  tiempoActivo: txt('Tiempo desde que se encendió el servidor de la app.', 'Time since the app server was started.'),
  motor: txt('Tipo de base de datos que usa el servidor.', 'Kind of database the server uses.'),
  conexion: txt('Si el servidor llega a la base de datos.', 'Whether the server reaches the database.'),
  migraciones: txt(
    'Actualizaciones de la estructura de la base: aplicadas y pendientes. Las pendientes deben ser 0.',
    'Updates to the database structure: applied and pending. Pending should be 0.',
  ),
  ubicacionBase: txt('Dónde está la base de datos.', 'Where the database is.'),
  tipoArchivos: txt(
    'Dónde se guardan los archivos subidos y generados: carpeta local o almacenamiento en la nube.',
    'Where uploaded and generated files are kept: local folder or cloud storage.',
  ),
  disponible: txt('Si el servidor puede usar los archivos.', 'Whether the server can use the files.'),
  ubicacionArchivos: txt('Dónde están los archivos.', 'Where the files are.'),
  fecha: txt(
    'Hora de esta consulta; se repite sola cada 15 segundos.',
    'Time of this check; it repeats on its own every 15 seconds.',
  ),
} satisfies Record<string, Texto>;

// ── Logros ──────────────────────────────────────────────────────────────────
export const AYUDA_LOGROS = {
  exportador: txt('Muestra solo los logros de un exportador.', 'Shows only the achievements of one exporter.'),
  numero: txt(
    'Número alcanzado. Exportaciones propias por el número del código; Big Bags de CNC en orden de empaque. La fecha es el zarpe (ETD) de la exportación o el empaque del Big Bag.',
    'Number reached. Own exports by the number in the code; CNC Big Bags in packing order. The date is the export’s departure (ETD) or the Big Bag’s packing date.',
  ),
} satisfies Record<string, Texto>;
